import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
// services
import { MessageService } from '../../../../services/message.service'
import { MasterService } from '../../../../services/master.service'
import { StoreService } from '../../../../services/store.service'
import * as languageLibrary from '../../../../services/language'

import { INVENTORY } from '../../../../constants/inventory'
import { Subscription } from 'rxjs';
import moment from 'moment';

import { formatCurrency, formatCount } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { minValueValidator } from 'src/app/utils/min-value.validator';
import { Lot } from 'src/app/models/lot.model';
import { Vendor } from 'src/app/models/vendor.model';
import { OdometerType } from 'src/app/models/odometer-type.model';
import { TitleCondition } from 'src/app/models/title-condition.model';
import { Color } from 'src/app/models/color.model';
import { VehicleType } from 'src/app/models/vehicle-type.model';
import { TitleCertifyLevel } from 'src/app/models/title-certify-level.model';
import { InventoryHistory } from 'src/app/models/inventory-history.model';

@Component({
    selector: 'inventory-general-information',
    templateUrl: './inventory-general-information.component.html',
    styleUrls: ['./inventory-general-information.component.scss'],
    standalone: false
})
export class InventoryGeneralInformationComponent implements OnInit, OnDestroy {

    /*
    * Variables
    */

    // get the recon cost from the reconditioning component
    @Input() reconCostTotal: number = 0

    // get the floor plan cost from the floor plan component
    @Input() floorPlanCostTotal: number = 0

    // save the language
    @Input() language: string = 'EN'

    @Input() isFlat = false

    @Input() cleanStockNumber

    // contact object
    @Input() data: Object = {}

    // contact object
    @Input() setup: Object = {}

    // permissions
    @Input() vehicleId: string | null = null

    // define the file URL
    @Input() fileURL: string = ''

    // define the inventory id
    @Input() inventoryId: number = 0
    
    // identify if called from sale
    @Input() calledFromSale: boolean = false

    // car types catalog
    @Input() types: string[] = []

    @Output() vehicleSelectedEvent = new EventEmitter<Number>()

    @Output() emitterInformation$: EventEmitter<Object> = new EventEmitter()

    // set all words
    protected words = languageLibrary.language

    // get the total cost
    protected costTotal: number = 0

    // get the purchase cost
    private costPurchase: number = 0

    protected inventoryStatusesDisplayable: any[];

    // define expanded as true
    protected expanded: boolean = true

    // image to display
    protected image = null

    protected duplicateVINFound: boolean;

    protected lots: Lot[] = [];

    private userId: number;

    protected vendors: Vendor[];

    protected paymentMethods: string[];

    protected markAsDeleted: boolean = false

    protected statusId: number;

    protected statusHistoryList: InventoryHistory[];

    protected featuresList: Record<string, Record<string, string | number | boolean | null>> = {};

    // lot Id
    protected lotId: number;

    // list of odometers
    protected odometers: OdometerType[] = INVENTORY.ODOMETER_TYPES

    protected titleConditions: TitleCondition[] = INVENTORY.TITLE_CONDITIONS
    
    // list of certify levels
    protected certifyLevels: TitleCertifyLevel[] = INVENTORY.CERTIFY_LEVELS

    // list of colors
    protected colors: Color[] = INVENTORY.COLORS

    // list of Vehicle Types
    protected vehicleTypes: VehicleType[] = INVENTORY.VEHICLE_TYPES

    /** The available options for an airbag status. */
    protected airbagStatuses = INVENTORY.AIRBAG_STATUSES

    // loading
    protected loading: boolean = false

    // Indicates the data is currently being saved (inserted or modified)
    private saving: boolean = false

    protected information = new FormGroup({
        vinNumber: new FormControl<string | null>(null, [Validators.required]),
        stockNumber: new FormControl<string | null>(null, [Validators.required]),
        miles: new FormControl<string | null>(null, [Validators.pattern('[0-9,]{1,20}')]),
        odometer: new FormControl<string>('ACTUAL', [Validators.required]),
        exteriorColor: new FormControl<string | null>(null),
        exteriorColorMinor: new FormControl<string | null>(null),
        interiorColor: new FormControl<string | null>(null),
        type: new FormControl<string | null>(null),
        modelYear: new FormControl<number | null>(null),
        make: new FormControl<string | null>(null),
        model: new FormControl<string | null>(null),
        cylinders: new FormControl<string | null>(null),
        transmission: new FormControl<string | null>(null),
        driveline: new FormControl<string | null>(null),
        bodyStyle: new FormControl<string | null>(null),
        trimLevel: new FormControl<string | null>(null),
        engine: new FormControl<string | null>(null),
        curbWeight: new FormControl<string | null>(null),
        carryingWeight: new FormControl<string | null>(null),
        gvwr: new FormControl<string | null>(null),
        tonnage: new FormControl<string | null>(null),
        lotId: new FormControl<number>(0),
        id: new FormControl<number>(0),
        vehicleId: new FormControl<number>(0),
        purchaseCost: new FormControl<string>(formatCurrency(0), minValueValidator(0)),
        totalFloorPlanCost: new FormControl<string>({ value: formatCurrency(0), disabled: true }),
        totalReconCost: new FormControl<string>({ value: formatCurrency(0), disabled: true }),
        totalCost: new FormControl<string>({ value: formatCurrency(0), disabled: true }),
        sellingPrice: new FormControl<string>(formatCurrency(0), minValueValidator(0)),
        writeDownCost: new FormControl<string>(formatCurrency(0), minValueValidator(0)),
        downPayment: new FormControl<string>(formatCurrency(0), minValueValidator(0)),
        bookValue: new FormControl<string>(formatCurrency(0), minValueValidator(0)),
        licensePlateNumber: new FormControl<string | null>(null),
        announcements: new FormControl<string | null>(null),
        dateTitleDeliveredCustomer: new FormControl<string | null>(null),
        dateOnLot: new FormControl<string>(new Date().toISOString().substring(0, 10), [Validators.required]),
        inventorySourceId: new FormControl<number | null>(null),
        source: new FormControl<string | null>(null),
        keyNumberAssigned: new FormControl<string | null>(null),
        airbagStatus: new FormControl<string | null>(null),
        inspectionDate: new FormControl<string | null>(null),
        datePurchased: new FormControl<string | null>(null),
        titleCondition: new FormControl<string | null>(null),
        inspectionDueDate: new FormControl<string | null>(null),
        datePaid: new FormControl<string | null>(null),
        titleLocation: new FormControl<string | null>(null),
        inspectionExpires: new FormControl<string | null>(null),
        paymentMethod: new FormControl<string | null>(null),
        titleNumber: new FormControl<string | null>(null),
        dateTitleReceived: new FormControl<string | null>(null),
        cleanStockNumber: new FormControl<string | null>(null),
        statusId: new FormControl<number>(1),
        certifyLevel: new FormControl<number | null>(null, [Validators.required]),
    })

    public deletable: boolean = false;

    /** Quick access to some inputs */
    
    get vinNumber() { return this.information.get('vinNumber') }
    get stockNumber() { return this.information.get('stockNumber') }
    
    get featuresExist() {
        return (this.featuresList && (Object.keys(this.featuresList).length > 0))
    }

    /*
    * life cycles
    */
    constructor(private router: Router, private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService) { }

    private subscriptions: Subscription[] = []

    ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)
        this.information.get("lotId").setValue(this.lotId)

        this.subscriptions.push(
            this.information.get('purchaseCost').valueChanges.subscribe(() => {
                this.calculateTotal()
            })
        )

        this.getLots()
        this.getPaymentMethods()
        this.getInventoryStatuses()
    }

    ngOnChanges(changes: SimpleChanges): void {
        //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        //Add '${implements OnChanges}' to the class.
        if (changes.reconCostTotal) {
            if (!changes.reconCostTotal.firstChange) {
                this.calculateTotal()
            }
        }

        if (changes.floorPlanCostTotal) {
            if (!changes.floorPlanCostTotal.firstChange) {
                this.calculateTotal()
            }
        }

        if (changes.inventoryId) {
            this.inventoryId = changes.inventoryId.currentValue
            this.fetch()
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    private reset = (): void => {
        this.information.reset()
        this.information.enable()
        this.information.get('lotId').setValue(this.lotId)
        this.information.get("dateOnLot").setValue(moment().format('YYYY-MM-DD'))
        this.information.get('stockNumber').setErrors(null)
        this.statusHistoryList = []
        this.featuresList = {}
        this.statusId = 0
        this.saving = false
        this.loading = false
    }

    /*
    * Read any saved details for this inventory item.
    */
    public fetch() {
        if (this.inventoryId <= 0) {
            this.reset()
            return
        }

        this.master.get(`inventory/${this.inventoryId}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.inventory)
            this.emitterInformation$.emit({ message: 'setInventoryId', inventoryId: this.inventoryId, stockNumber: res.data.inventory.stockNumber, vehicleId: res.data.inventory.vehicleId })
            this.emitterInformation$.emit({ message: 'setDetailInfo', detailInfo: { createdBy: res.data.inventory.createdByUser ? res.data.inventory.createdByUser.firstName + " " + res.data.inventory.createdByUser.lastName : null, modifiedBy: res.data.inventory.modifiedByUser ? res.data.inventory.modifiedByUser.firstName + " " + res.data.inventory.modifiedByUser.lastName : null, createdAt: res.data.inventory.createdAt, updatedAt: res.data.inventory.updatedAt } })
            this.emitterInformation$.emit({ message: 'setFPDefaultData', purchaseCost: res.data.inventory.purchaseCost, dateOnLot: res.data.inventory.dateOnLot })
        })
    }

    public deleteInventory() {
        if (this.statusId === 6) {
            return
        }

        this.master.discard(`inventory/${this.inventoryId}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            (document.getElementById('btn-delete-generalInformation') as HTMLButtonElement).click();
            this.router.navigate([['/inventory']])
        })
    }

    public saveInventory(automated: boolean = false) {
        if (this.inventoryId <= 0) {
            this.createInventory(automated)
        }

        this.modifyInventory(true)
    }

    public modifyInventory(setInformation: boolean = false) {
        if (this.inventoryId <= 0) {
            return
        }

        if (this.saving) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.information.value))
        if (parseInt(payload['id']) == 0) {
            return
        }

        payload['purchaseCost'] = clean(payload['purchaseCost'])
        payload['totalFloorPlanCost'] = this.floorPlanCostTotal || 0
        payload['totalReconCost'] = this.reconCostTotal || 0
        payload['totalCost'] = this.costTotal || 0
        payload['writeDownCost'] = clean(payload['writeDownCost'])
        payload['downPayment'] = clean(payload['downPayment'])
        payload['bookValue'] = clean(payload['bookValue'])
        payload['sellingPrice'] = clean(payload['sellingPrice'])
        payload['tonnage'] = (payload['tonnage'] && (payload['tonnage'].length > 0)) ? payload['tonnage'] : null
        payload['curbWeight'] = clean(payload['curbWeight'])
        payload['carryingWeight'] = clean(payload['carryingWeight'])
        payload['gvwr'] = clean(payload['gvwr'])
        payload['miles'] = clean(payload['miles'])

        this.saving = true
        this.master.put(`inventory/${this.inventoryId}`, { inventoryData: payload, features: this.featuresList }, res => {
            this.saving = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status === 409) {
                this.information.controls['stockNumber'].setErrors({ 'duplicateFound': "Error! Stock Number is taken." });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (!setInformation) {
                return
            }

            this.setInformation(res.data.inventory)
            this.emitterInformation$.emit({ message: 'setInventoryId', inventoryId: this.inventoryId, stockNumber: res.data.inventory.stockNumber })
            this.emitterInformation$.emit({ message: 'setDetailInfo', detailInfo: { modifiedBy: res.data.inventory.modifiedByUser ? res.data.inventory.modifiedByUser.firstName + " " + res.data.inventory.modifiedByUser.lastName : null, createdAt: res.data.inventory.createdAt, createdBy: res.data.inventory.createdByUser.firstName + " " + res.data.inventory.createdByUser.lastName, updatedAt: res.data.inventory.updatedAt } })
            this.emitterInformation$.emit({ message: 'setFPDefaultData', purchaseCost: res.data.inventory.purchaseCost, dateOnLot: res.data.inventory.dateOnLot })
        })
    }

    public createInventory(automated: boolean = false) {
        if (this.saving) {
            return
        }

        this.calculateTotal() // ensure we have the total cost

        const payload = JSON.parse(JSON.stringify(this.information.value))
        payload['purchaseCost'] = clean(payload['purchaseCost'])
        payload['totalFloorPlanCost'] = clean(payload['totalFloorPlanCost'])
        payload['totalCost'] = clean(payload['totalCost'])
        payload['writeDownCost'] = clean(payload['writeDownCost'])
        payload['downPayment'] = clean(payload['downPayment'])
        payload['bookValue'] = clean(payload['bookValue'])
        payload['sellingPrice'] = clean(payload['sellingPrice'])
        payload['tonnage'] = (payload['tonnage'] && (payload['tonnage'].length > 0)) ? payload['tonnage'] : null
        payload['curbWeight'] = clean(payload['curbWeight'])
        payload['carryingWeight'] = clean(payload['carryingWeight'])
        payload['gvwr'] = clean(payload['gvwr'])
        payload['miles'] = clean(payload['miles'])

        this.saving = true
        this.master.post(`inventory/`, { inventoryData: payload, features: this.featuresList }, res => {
            this.saving = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status === 409) {
                this.information.controls['stockNumber'].setErrors({ 'duplicateFound': "Error! Stock Number is taken." });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.inventory)
            this.emitterInformation$.emit({ message: 'setInventoryId', inventoryId: this.inventoryId, stockNumber: res.data.inventory.stockNumber })
            this.emitterInformation$.emit({ message: 'setDetailInfo', detailInfo: { createdBy: res.data.inventory.createdByUser.firstName + " " + res.data.inventory.createdByUser.lastName, modifiedBy: null, createdAt: res.data.inventory.createdAt, updatedAt: null } })
            this.emitterInformation$.emit({ message: 'setFPDefaultData', purchaseCost: res.data.inventory.purchaseCost, dateOnLot: res.data.inventory.dateOnLot })
            this.emitterInformation$.emit({ message: 'addedVehicle', inventoryId: this.inventoryId, stockNumber: res.data.inventory.stockNumber, automated: automated })
            this.vehicleSelectedEvent.emit(this.inventoryId)

            if (this.calledFromSale) { // don't reroute if in a modal
                return
            }

            this.router.navigate([`/inventory/${this.inventoryId}`])
        })
    }

    public async setInformation(inventory: []) {
        if (!inventory || inventory.length <= 0) return

        this.inventoryId = inventory['id']
        this.statusId = inventory['statusId']
        this.information.setValue({
            vinNumber: inventory['vehicle'].vinNumber || null,
            stockNumber: inventory['stockNumber'] || null,
            miles: formatCount(inventory['miles'] || 0),
            odometer: inventory['odometer'] || null,
            exteriorColor: inventory['exteriorColor'] || null,
            exteriorColorMinor: inventory['exteriorColorMinor'] || null,
            interiorColor: inventory['interiorColor'] || null,
            type: inventory['vehicle'].type || null,
            modelYear: inventory['vehicle'].modelYear || null,
            make: inventory['vehicle'].make || null,
            model: inventory['vehicle'].model || null,
            cylinders: inventory['vehicle'].cylinders || null,
            transmission: inventory['vehicle'].transmission || null,
            driveline: inventory['vehicle'].driveline || null,
            bodyStyle: inventory['vehicle'].bodyStyle || null,
            trimLevel: inventory['vehicle'].trimLevel || null,
            engine: inventory['vehicle'].engine || null,
            curbWeight: formatCount(inventory['vehicle'].curbWeight || 0),
            carryingWeight: formatCount(inventory['vehicle'].carryingWeight || 0),
            gvwr: formatCount(inventory['vehicle']['gvwr'] || 0),
            tonnage: inventory['vehicle'].tonnage || '',
            lotId: inventory['lotId'] || null,
            id: inventory['id'] || null,
            vehicleId: inventory['vehicleId'] || null,
            purchaseCost: formatCurrency(inventory['purchaseCost'] || 0),
            totalFloorPlanCost: formatCurrency(inventory['totalFloorPlanCost'] || 0),
            totalReconCost: formatCurrency(inventory['totalReconCost'] || 0),
            totalCost: formatCurrency(inventory['totalCost'] || 0),
            sellingPrice: formatCurrency(inventory['sellingPrice'] || 0),
            writeDownCost: formatCurrency(inventory['writeDownCost'] || 0),
            downPayment: formatCurrency(inventory['downPayment'] || 0),
            bookValue: formatCurrency(inventory['bookValue'] || 0),
            licensePlateNumber: inventory['licensePlateNumber'] || null,
            announcements: inventory['announcements'] || null,
            dateTitleDeliveredCustomer: inventory['dateTitleDeliveredCustomer'] || null,
            dateOnLot: inventory['dateOnLot'] || null,
            inventorySourceId: inventory['inventorySourceId'] || null,
            source: inventory['source'] || null,
            keyNumberAssigned: inventory['keyNumberAssigned'] || null,
            airbagStatus: inventory['airbagStatus'] || null,
            inspectionDate: inventory['inspectionDate'] || null,
            datePurchased: inventory['datePurchased'] || null,
            titleCondition: inventory['titleCondition'] || null,
            inspectionDueDate: inventory['inspectionDueDate'] || null,
            datePaid: inventory['datePaid'] || null,
            titleLocation: inventory['titleLocation'] || null,
            inspectionExpires: inventory['inspectionExpires'] || null,
            paymentMethod: inventory['paymentMethod'] || null,
            titleNumber: inventory['titleNumber'] || null,
            dateTitleReceived: inventory['dateTitleReceived'] || null,
            cleanStockNumber: inventory['cleanStockNumber'] || null,
            statusId: inventory['statusId'],
            certifyLevel: (inventory['certifyLevel'] || 'v').toLowerCase()
        })

        this.featuresList = inventory['vehicle'].features
        
        this.calculateDaysOld()
        this.costTotal = inventory['purchaseCost']

        this.information.enable()
        if (this.statusId === 6) { // 6 = Deleted
            this.information.disable()
        }

        this.showCurrentStatus(this.statusId, inventory['inventoryStatus'].selectable)
    }

    public async decodeVIN(stockNumber: string, vinNumber: string) {
        this.information.get('vinNumber').setValue(vinNumber.toUpperCase())
        if (this.markAsDeleted || !vinNumber) {
            return
        }

        // set stock Number and check duplicate VIN
        await this.setStockNumber(stockNumber, vinNumber)

        if (this.duplicateVINFound) {
            return
        }

        //decode VIN here
        this.decode(vinNumber)
    }

    private decode(vinNumber: string) {
        this.master.get(`vendors/vin/decode?vinNumber=${vinNumber.toUpperCase()}&lotId=${this.lotId}`, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.data.error) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            const decoded = res.data
            this.information.get("modelYear").setValue(decoded.modelYear)
            this.information.get("make").setValue(decoded.make)
            this.information.get("model").setValue(decoded.model)
            this.information.get("cylinders").setValue(decoded.cylinders || null)
            this.information.get("transmission").setValue(decoded.transmission || null)
            this.information.get("driveline").setValue(decoded.driveline || null)
            this.information.get("trimLevel").setValue(decoded.trimLevel || null)
            this.information.get("bodyStyle").setValue(decoded.bodyStyle || null)
            this.information.get("engine").setValue(decoded.engine || null)
            this.information.get("curbWeight").setValue(decoded.curbWeight || null)
            this.information.get("type").setValue(decoded.type || null)
            this.featuresList = decoded.features
            this.saveInventory(true)
        })
    }

    private async setStockNumber(stockNumber: string, vinNumber: string): Promise<any> {
        if (stockNumber !== "" && !this.information.controls['stockNumber'].hasError) {
            return
        }

        const ctx = this
        this.information.get('stockNumber')?.setErrors(null)
        return await this.master.getAsync(`inventory/stock/next?lotId=${this.lotId}&vinNumber=${vinNumber}`).then( async (res) => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            const info = res.data.inventoryStockData
            if (!info) {
                return
            }

            const stockNumber = info.preStockNumber ? isNaN(info.preStockNumber) ? info.preStockNumber.toUpperCase() : info.preStockNumber : null
            this.information.get("stockNumber").setValue(stockNumber)
            this.information.get("cleanStockNumber").setValue(stockNumber)
            this.information.get("certifyLevel").setValue((res.data.certifyLevel || 'v').toLowerCase())

            return await ctx.checkDuplicateVIN(this.inventoryId, stockNumber, vinNumber)
        })
    }

    private async checkDuplicateVIN(inventoryId: number, stockNumber: string, vinNumber: string): Promise<any> {
        if (!vinNumber || (vinNumber.length <= 0)) {
            return
        }

        this.duplicateVINFound = false
        return await this.master.getAsync(`inventory/vin/exists?vinNumber=${vinNumber}&inventoryId=${inventoryId}&stockNumber=${stockNumber}`).then((res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status === 200) {
                return
            }

            this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
            this.duplicateVINFound = (res.status === 409)
        })
    }

    public showCurrentStatus(statusId: number, deletable: boolean) {
        if (this.information.get('statusId').value !== statusId) {
            let control = (document.getElementById("inventoryStatus_" + this.information.get('statusId').value) as HTMLInputElement);
            if (!control || control.value == 'undefined') {
                return
            }
            control.classList.remove("current");
        }
        this.deletable = deletable
        this.information.get('statusId').setValue(statusId)
        switch (statusId) {
            case 1:
                document.getElementById("process-line").style.setProperty("width", "0%");
                break;
            case 2:
                document.getElementById("process-line").style.setProperty("width", "25%");
                break;
            case 3:
                document.getElementById("process-line").style.setProperty("width", "50%");
                break;
            case 4:
                document.getElementById("process-line").style.setProperty("width", "75%");
                break;
            case 5:
                document.getElementById("process-line").style.setProperty("width", "100%");
                break;
            case 7:
                // change purchase to trade-in
                document.getElementById("process-line").style.setProperty("width", "0%");
                break;
            default:
                break;
        }

        const statusElement = document.getElementById("inventoryStatus_" + statusId)
        if (!statusElement) {
            return
        }

        statusElement.classList.add('current');
    }

    public listenerChildrens = (e): void => {
        switch (e.message) {
            case 'showNewStatusId':
                this.statusId = e.statusId
                this.showCurrentStatus(this.statusId, true)
                break
            case 'saveScannedVIN':
                this.information.get("vinNumber").setValue(e.vinNumber)
                this.decodeVIN("", e.vinNumber)
                break;
            case 'failedScannedVIN':
                this.information.controls['vinNumber'].setErrors(e.error);
                break;
            default:
                break;
        }
    }

    public calculateDaysOld = () => {
        let dateOnLot = moment(this.information.get('dateOnLot').value);
        if (!moment(dateOnLot).isValid()) {
            return 0
        }
        let now = moment(moment().format('YYYY-MM-DD'))
        return now.diff(dateOnLot, 'days')
    }

    public getVendors() {
        //used for inventory source
        this.master.get(`contacts?type=vendor`, (res) => {
            this.vendors = [];
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.vendors = res.data.contacts
        })
    }

    public getLots() {
        //used for inventory source
        this.master.get(`lots`, (res) => {
            this.lots = [];
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            this.lots = res.data.lots
        })
    }

    public calculateTotal() {
        this.costTotal = (this.reconCostTotal ? this.reconCostTotal : 0) + (this.floorPlanCostTotal ? this.floorPlanCostTotal : 0) + clean(this.information.get('purchaseCost').value)
        this.information.get('totalCost').setValue(formatCurrency(this.costTotal))
    }

    public restoreInventory() {
        this.master.patch(`inventory/${this.inventoryId}/restore`, null, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.fetch();
            (document.getElementById('btn-restore-generalInformation') as HTMLButtonElement).click();
            this.information.enable();
        })
    }

    public getInventoryStatuses() {

        this.master.get(`collections/inventory/statuses?displayable=true`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.inventoryStatusesDisplayable = res.data.inventoryStatuses
        })
    }

    public getPaymentMethods() {
        this.master.get(`collections/payment/methods`, (res) => {
            this.paymentMethods = [];
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.paymentMethods = res.data
        })
    }

    public dateInPast(startDate: string) {
        return (group: FormGroup): { [key: string]: any } => {
            let start = group.controls[startDate]
            if (start.value >= moment().format()) {
                return {
                    dates: "Start date from should be in the past"
                };
            }
            return
        }
    }

    public updateInspectionExpires() {
        this.information.patchValue({
            inspectionExpires: moment(this.information.get('inspectionDate').value).add(1, 'year').format('YYYY-MM-DD'),
            inspectionDueDate: moment(this.information.get('inspectionDate').value).add(90, 'days').format('YYYY-MM-DD'),
        })
    }

    public updateGVWR() {
        const curbWeight = clean(this.information.get('curbWeight').value)
        const carryWeight = clean(this.information.get('carryingWeight').value)

        if ((curbWeight <= 0) || (carryWeight <= 0)) {
            this.information.get('gvwr').setValue('0')
            return
        }
        
        this.information.get('gvwr').setValue(formatCount(curbWeight + carryWeight))
    }

    public changeFeature(feature: any): void {
        this.featuresList[`${feature.key}`] = {
            unit: this.featuresList[`${feature.key}`].unit || null,
            value: (document.getElementById(`${feature.key}`) as HTMLInputElement).value
        }
    }

    public notifyUser() {
        navigator.clipboard.writeText(this.information.get('vinNumber').value).then(() => {
            this.ms.sendMessage("alert", { type: "success", text: `The VIN number is already on your clipboard. Just paste the VIN.` });
            /* Resolved - text copied to clipboard successfully */
        }, () => {
            console.error('Failed to copy');
            /* Rejected - text failed to copy to the clipboard */
        });

    }

    public setContactIdEvent(e) {
        if (!e) {
            return
        }
        this.information.get("inventorySourceId").setValue(e)
    }
}


